import { Fragment, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { formatPct, formatRevenueCompact, formatRevenueFull } from './formatters'
import type { NodeRevenueEntry } from './NodeSearchPanel'

export const GRID_TEMPLATE = '1fr 170px 70px'

export function HeaderCell({
  children,
  align = 'left',
  paddingRight
}: {
  children: ReactNode
  align?: 'left' | 'right' | 'center'
  paddingRight?: number
}) {
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
  return (
    <div
      className="px-3 py-1.5 border-b text-[11px] uppercase tracking-wide font-medium sticky top-0 flex items-center"
      style={{
        borderColor: 'var(--border)',
        color: 'var(--muted-foreground)',
        background: 'var(--background)',
        justifyContent: justify,
        textAlign: align,
        paddingRight: paddingRight ?? undefined,
        zIndex: 1
      }}
    >
      {children}
    </div>
  )
}

export function ParentRow({
  name,
  share,
  revenue,
  currency
}: {
  name: string
  share: number
  revenue: string
  currency?: string | null
}) {
  return (
    <div
      className="grid border-b"
      style={{
        gridTemplateColumns: GRID_TEMPLATE,
        borderColor: 'var(--border)',
        background: 'var(--muted, transparent)'
      }}
    >
      <div className="py-2 px-3 min-w-0 flex flex-col justify-center">
        <div className="text-sm break-words" style={{ color: 'var(--foreground)', fontWeight: 600 }}>
          {name}
        </div>
      </div>
      <div className="py-2 px-3 flex flex-col items-end justify-center">
        <div className="text-sm tabular-nums" style={{ color: 'var(--foreground)', fontWeight: 600 }}>
          {formatPct(share)}
        </div>
        <div
          className="text-xs tabular-nums"
          style={{ color: 'var(--muted-foreground)' }}
          title={formatRevenueFull(revenue, currency)}
        >
          {formatRevenueCompact(revenue, currency)}
        </div>
      </div>
      <div className="py-2 flex items-center justify-center">
        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>—</div>
      </div>
    </div>
  )
}

export function OtherRow({
  added,
  disabled = false,
  onAdd
}: {
  added: boolean
  disabled?: boolean
  onAdd: () => void
}) {
  const cellStyle = { borderColor: 'var(--border)' }
  const labelColor = disabled ? 'var(--muted-foreground)' : 'var(--foreground)'
  return (
    <Fragment>
      <div className="border-b py-2 px-3 min-w-0 flex flex-col justify-center" style={cellStyle}>
        <div className="text-sm break-words" style={{ color: labelColor, fontWeight: 500 }}>
          Other
        </div>
      </div>
      <div
        className="border-b py-2 px-3 flex items-center justify-end"
        style={cellStyle}
      >
        <div className="text-sm tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
          —
        </div>
      </div>
      <div className="border-b py-2 flex items-center justify-center" style={cellStyle}>
        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={onAdd}
            disabled={added}
          >
            {added ? 'Added' : 'Add'}
          </Button>
        )}
      </div>
    </Fragment>
  )
}

export function NewProductRow({
  added,
  onAdd
}: {
  added: boolean
  onAdd: () => void
}) {
  const cellStyle = { borderColor: 'var(--border)' }
  return (
    <Fragment>
      <div className="border-b py-2 px-3 min-w-0 flex flex-col justify-center" style={cellStyle}>
        <div className="text-sm break-words" style={{ color: 'var(--foreground)', fontWeight: 500 }}>
          New Products
        </div>
      </div>
      <div
        className="border-b py-2 px-3 flex items-center justify-end"
        style={cellStyle}
      >
        <div className="text-sm tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
          —
        </div>
      </div>
      <div
        className="border-b py-2 flex items-center justify-center"
        style={cellStyle}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={onAdd}
          disabled={added}
        >
          {added ? 'Added' : 'Add'}
        </Button>
      </div>
    </Fragment>
  )
}

export function EntryCells({
  entry,
  added,
  currency,
  onAdd
}: {
  entry: NodeRevenueEntry
  added: boolean
  currency?: string | null
  onAdd: (e: NodeRevenueEntry) => void
}) {
  const cellStyle = { borderColor: 'var(--border)' }
  const noRevenue = !Number(entry.revenue)
  return (
    <Fragment>
      <div className="border-b py-2 px-3 min-w-0 flex flex-col justify-center" style={cellStyle}>
        <div className="text-sm break-words" style={{ color: 'var(--foreground)', fontWeight: 500 }}>
          {entry.name}
        </div>
      </div>
      <div
        className="border-b py-2 px-3 flex flex-col items-end justify-center"
        style={cellStyle}
      >
        {noRevenue ? (
          <div className="text-sm tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
            —
          </div>
        ) : (
          <>
            <div className="text-sm tabular-nums" style={{ color: 'var(--foreground)' }}>
              {formatPct(entry.share)}
            </div>
            <div
              className="text-xs tabular-nums"
              style={{ color: 'var(--muted-foreground)' }}
              title={formatRevenueFull(entry.revenue, currency)}
            >
              {formatRevenueCompact(entry.revenue, currency)}
            </div>
          </>
        )}
      </div>
      <div
        className="border-b py-2 flex items-center justify-center"
        style={cellStyle}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => !added && onAdd(entry)}
          disabled={added}
        >
          {added ? 'Added' : 'Add'}
        </Button>
      </div>
    </Fragment>
  )
}
